/**
 * Trailer–cargo incompatibilities on trip DAG edges (unanimity rule) and reachability diagnostics.
 * Used by trip LP, supply-flow validation, and infeasibility messaging.
 */

import type { RouteStructure } from './routeInference';
import { getSegmentNodeIdForDag } from './tripDag';
import { buildRequirementsFromPackingLists, type DagNodeRow } from './tripLpRequirements';

/** Category name used to identify trailer item types (same as route inference). */
export const TRAILERS_CATEGORY_NAME = 'Trailers';

export type TrailerCargoIncompatibilityPair = { trailerId: string; cargoId: string };

/** Normalize DB rows to pairs for LP and fingerprinting. */
export function incompatibilityPairsFromRows(
    rows:
        | Array<
              | {
                    trailerItemType?: { id?: string } | null;
                    cargoItemType?: { id?: string } | null;
                }
              | null
              | undefined
          >
        | null
        | undefined
): TrailerCargoIncompatibilityPair[] {
    if (!rows?.length) return [];
    const out: TrailerCargoIncompatibilityPair[] = [];
    for (const r of rows) {
        const tid = r?.trailerItemType?.id;
        const cid = r?.cargoItemType?.id;
        if (tid && cid) out.push({ trailerId: tid, cargoId: cid });
    }
    return out;
}

export type DagNodeForTrailerMap = {
    index: number;
    kind: 'source' | 'event' | 'dest';
    eventId?: string | null;
    storageLocationId?: string | null;
};

export type TripDagEdgeLike = { fromNodeIndex: number; toNodeIndex: number };

/** Stable key for a DAG edge by node indices (matches LP variable pairing). */
export function edgeIndexKey(fromNodeIndex: number, toNodeIndex: number): string {
    return `${fromNodeIndex}\t${toNodeIndex}`;
}

export function makeIncompatibilityKey(trailerId: string, cargoId: string): string {
    return `${trailerId}\t${cargoId}`;
}

/** Build a set of normalized pair keys for O(1) incompatibility checks. */
export function buildIncompatibilityKeySet(pairs: TrailerCargoIncompatibilityPair[]): Set<string> {
    const s = new Set<string>();
    for (const p of pairs) {
        s.add(makeIncompatibilityKey(p.trailerId, p.cargoId));
    }
    return s;
}

/**
 * Map logical DAG node id (same strings as buildTripDagFromRouteStructure / SupplyFlowSection) → node index.
 */
export function buildNodeIdToIndexFromDagNodes(
    dagNodes: DagNodeForTrailerMap[],
    sourceStorageLocationId: string,
    destStorageLocationId: string
): Map<string, number> {
    const sourceId = `storage:source:${sourceStorageLocationId}`;
    const destId = `storage:dest:${destStorageLocationId}`;
    const m = new Map<string, number>();
    for (const n of dagNodes) {
        let id: string;
        if (n.kind === 'source') id = sourceId;
        else if (n.kind === 'dest') id = destId;
        else id = n.eventId ?? '';
        m.set(id, n.index);
    }
    return m;
}

/**
 * For each DAG edge (by indices), the set of trailer item type ids whose route uses that edge.
 * Empty set on an edge means no unanimity ban on that edge.
 */
export function buildTrailersPerEdge(
    routeStructure: RouteStructure,
    dagNodes: DagNodeForTrailerMap[],
    sourceStorageLocationId: string,
    destStorageLocationId: string
): Map<string, Set<string>> {
    const nodeIdToIndex = buildNodeIdToIndexFromDagNodes(dagNodes, sourceStorageLocationId, destStorageLocationId);
    const map = new Map<string, Set<string>>();
    for (const path of routeStructure.trailerPaths.values()) {
        const len = path.segments.length;
        for (let i = 0; i < len - 1; i++) {
            const fromId = getSegmentNodeIdForDag(path.segments[i], i, len);
            const toId = getSegmentNodeIdForDag(path.segments[i + 1], i + 1, len);
            const fi = nodeIdToIndex.get(fromId);
            const ti = nodeIdToIndex.get(toId);
            if (fi === undefined || ti === undefined) continue;
            const key = edgeIndexKey(fi, ti);
            let set = map.get(key);
            if (!set) {
                set = new Set();
                map.set(key, set);
            }
            set.add(path.trailerId);
        }
    }
    return map;
}

/**
 * Unanimity: ban cargo on edge iff every trailer on that edge is incompatible with cargo.
 * If no trailers use the edge, do not ban.
 */
export function isCargoBannedOnEdge(
    cargoItemTypeId: string,
    fromNodeIndex: number,
    toNodeIndex: number,
    trailersPerEdge: Map<string, Set<string>>,
    incompatibilityKeys: Set<string>
): boolean {
    const key = edgeIndexKey(fromNodeIndex, toNodeIndex);
    const trailers = trailersPerEdge.get(key);
    if (!trailers || trailers.size === 0) return false;
    for (const t of trailers) {
        if (!incompatibilityKeys.has(makeIncompatibilityKey(t, cargoItemTypeId))) {
            return false;
        }
    }
    return true;
}

/** Banned DAG edges for one cargo item type (edge index keys). */
export function bannedEdgeKeysForCargoItem(
    cargoItemTypeId: string,
    edges: TripDagEdgeLike[],
    trailersPerEdge: Map<string, Set<string>>,
    incompatibilityKeys: Set<string>
): Set<string> {
    const out = new Set<string>();
    for (const e of edges) {
        if (isCargoBannedOnEdge(cargoItemTypeId, e.fromNodeIndex, e.toNodeIndex, trailersPerEdge, incompatibilityKeys)) {
            out.add(edgeIndexKey(e.fromNodeIndex, e.toNodeIndex));
        }
    }
    return out;
}

export type ReachabilityDiagnostic = {
    unreachableEventIds: string[];
    unreachableEventLabels: string[];
    /** Node indices reachable from source (index 0) when traversing only non-banned edges. */
    reachableNodeIndices: Set<number>;
};

/** When value > 0, returns an error message if this edge is banned for the item; otherwise null. */
export function validateTripEdgeFlowChange(
    cargoItemTypeId: string,
    fromNodeIndex: number,
    toNodeIndex: number,
    value: number,
    trailersPerEdge: Map<string, Set<string>>,
    incompatibilityKeys: Set<string>
): string | null {
    if (value <= 0) return null;
    if (isCargoBannedOnEdge(cargoItemTypeId, fromNodeIndex, toNodeIndex, trailersPerEdge, incompatibilityKeys)) {
        return 'Cannot assign flow on this leg for this item: every trailer using this leg is incompatible with it.';
    }
    return null;
}

export type InfeasibilityFormatting = {
    getCargoName?: (id: string) => string;
    getTrailerName?: (id: string) => string;
    /** Label for a DAG node index (source storage, event, or destination storage). */
    getNodeLabel?: (nodeIndex: number) => string;
};

/** Edges on the boundary from reachable to unreachable subgraph (under current bans). */
function cutEdgesFromReachability(edges: TripDagEdgeLike[], reachableNodeIndices: Set<number>): Array<{ from: number; to: number }> {
    const out: Array<{ from: number; to: number }> = [];
    for (const e of edges) {
        if (reachableNodeIndices.has(e.fromNodeIndex) && !reachableNodeIndices.has(e.toNodeIndex)) {
            out.push({ from: e.fromNodeIndex, to: e.toNodeIndex });
        }
    }
    return out;
}

/**
 * Find a cut edge that blocks `targetNodeIndex` when walking backward from that node toward the source.
 */
export function findCutEdgeForEvent(
    cutEdges: Array<{ from: number; to: number }>,
    edges: TripDagEdgeLike[],
    targetNodeIndex: number
): { from: number; to: number } | null {
    const rev = new Map<number, number[]>();
    for (const e of edges) {
        const arr = rev.get(e.toNodeIndex) ?? [];
        arr.push(e.fromNodeIndex);
        rev.set(e.toNodeIndex, arr);
    }
    const queue: number[] = [targetNodeIndex];
    const visited = new Set<number>([targetNodeIndex]);
    while (queue.length > 0) {
        const cur = queue.shift();
        if (cur === undefined) throw new Error('findCutEdgeForEvent: queue unexpectedly empty');
        const cut = cutEdges.find(c => c.to === cur);
        if (cut) return cut;
        for (const pred of rev.get(cur) ?? []) {
            if (!visited.has(pred)) {
                visited.add(pred);
                queue.push(pred);
            }
        }
    }
    return null;
}

type BuildInfeasibilityOptions = {
    /** When LP failed but reachability found no blocked demand (e.g. other failure mode), keep the generic hint. */
    includeGenericFallbackWhenNoUnreachableDemand?: boolean;
};

/** One built row before attaching cargoItemTypeId (for collect / describe). */
export type InfeasibilityIssuePart = {
    message: string;
    /** Present for standard “from A to B via trailer(s): …” rows (for inventory links in UI). */
    transport?: { fromLabel: string; destLabel: string; trailerIds: string[] };
};

function buildInfeasibilityMessagesForCargoItem(
    cargoItemTypeId: string,
    dagNodes: DagNodeForTrailerMap[],
    edges: TripDagEdgeLike[],
    requirementsAtNodes: number[],
    trailersPerEdge: Map<string, Set<string>>,
    incompatibilityKeys: Set<string>,
    getEventLabel: (eventId: string) => string,
    formatting?: InfeasibilityFormatting,
    options?: BuildInfeasibilityOptions
): InfeasibilityIssuePart[] {
    const banned = bannedEdgeKeysForCargoItem(cargoItemTypeId, edges, trailersPerEdge, incompatibilityKeys);
    const { unreachableEventIds, reachableNodeIndices } = reachabilityFromSourceExcludingBannedEdges(
        edges,
        banned,
        dagNodes,
        getEventLabel
    );
    const unreachableWithDemand = unreachableEventIds.filter(eid => {
        const idx = dagNodes.findIndex(n => n.kind === 'event' && n.eventId === eid);
        return idx >= 0 && (requirementsAtNodes[idx] ?? 0) > 0;
    });
    if (unreachableWithDemand.length === 0) {
        if (banned.size > 0 && options?.includeGenericFallbackWhenNoUnreachableDemand) {
            return [
                {
                    message:
                        'The flow problem could not be solved for this item. Try relaxing trailer–cargo incompatibilities, packing lists, or trip routes.',
                },
            ];
        }
        return [];
    }

    const cutEdges = cutEdgesFromReachability(edges, reachableNodeIndices);
    const cargoName = formatting?.getCargoName?.(cargoItemTypeId) ?? cargoItemTypeId;
    const getTrailerName = formatting?.getTrailerName ?? ((id: string) => id);
    const getNodeLabel = formatting?.getNodeLabel;

    const out: InfeasibilityIssuePart[] = [];
    for (const eid of unreachableWithDemand) {
        const eventIdx = dagNodes.findIndex(n => n.kind === 'event' && n.eventId === eid);
        if (eventIdx < 0) continue;
        const destLabel = getEventLabel(eid);
        const cut = findCutEdgeForEvent(cutEdges, edges, eventIdx);
        if (!cut || !getNodeLabel) {
            out.push({
                message: `Cannot transport ${cargoName} to ${destLabel}: every path from the source crosses a leg where all trailers are incompatible with this item.`,
            });
            continue;
        }
        const fromLabel = getNodeLabel(cut.from);
        const trailerIds = Array.from(trailersPerEdge.get(edgeIndexKey(cut.from, cut.to)) ?? []);
        const trailerNames = trailerIds.map(getTrailerName).filter(Boolean).join(', ');
        const via = trailerNames.length > 0 ? ` via trailer(s): ${trailerNames}` : ' via trailer(s): (no trailers assigned on this leg)';
        out.push({
            message: `Cannot transport ${cargoName} from ${fromLabel} to ${destLabel}${via}`,
            transport: { fromLabel, destLabel, trailerIds },
        });
    }
    return out;
}

/**
 * Plain-language explanation when the LP is infeasible for one cargo item (reachability under bans).
 */
export function describeInfeasibilityForCargoItem(
    cargoItemTypeId: string,
    dagNodes: DagNodeForTrailerMap[],
    edges: TripDagEdgeLike[],
    requirementsAtNodes: number[],
    routeStructure: RouteStructure,
    incompatibilityPairs: TrailerCargoIncompatibilityPair[],
    sourceStorageLocationId: string,
    destStorageLocationId: string,
    getEventLabel: (eventId: string) => string,
    formatting?: InfeasibilityFormatting
): string {
    const keys = buildIncompatibilityKeySet(incompatibilityPairs);
    const trailersPerEdge = buildTrailersPerEdge(routeStructure, dagNodes, sourceStorageLocationId, destStorageLocationId);
    const parts = buildInfeasibilityMessagesForCargoItem(
        cargoItemTypeId,
        dagNodes,
        edges,
        requirementsAtNodes,
        trailersPerEdge,
        keys,
        getEventLabel,
        formatting,
        { includeGenericFallbackWhenNoUnreachableDemand: true }
    );
    return parts.map(p => p.message).join(' ');
}

export type InfeasibilityIssue = {
    cargoItemTypeId: string;
    message: string;
    transport?: { fromLabel: string; destLabel: string; trailerIds: string[] };
};

/**
 * One issue row per unreachable destination (and cargo) for the Issues tab.
 */
export function collectTrailerCargoInfeasibilityIssues(params: {
    dagNodes: DagNodeForTrailerMap[];
    edges: TripDagEdgeLike[];
    trailersPerEdge: Map<string, Set<string>>;
    incompatibilityKeys: Set<string>;
    packingListsByEvent: Map<string, Array<{ itemTypeId: string; quantity: number }>>;
    getCargoName: (id: string) => string;
    getTrailerName: (id: string) => string;
    getNodeLabel: (nodeIndex: number) => string;
    getEventLabel: (eventId: string) => string;
}): InfeasibilityIssue[] {
    const {
        dagNodes,
        edges,
        trailersPerEdge,
        incompatibilityKeys,
        packingListsByEvent,
        getCargoName,
        getTrailerName,
        getNodeLabel,
        getEventLabel,
    } = params;
    if (edges.length === 0 || dagNodes.length === 0) return [];

    const requirementsByItemType = buildRequirementsFromPackingLists(dagNodes as DagNodeRow[], packingListsByEvent);
    const issues: InfeasibilityIssue[] = [];

    for (const [cargoItemTypeId, requirementsAtNodes] of Object.entries(requirementsByItemType)) {
        const parts = buildInfeasibilityMessagesForCargoItem(
            cargoItemTypeId,
            dagNodes,
            edges,
            requirementsAtNodes,
            trailersPerEdge,
            incompatibilityKeys,
            getEventLabel,
            { getCargoName, getTrailerName, getNodeLabel }
        );
        for (const part of parts) {
            issues.push({ cargoItemTypeId, message: part.message, transport: part.transport });
        }
    }
    return issues;
}

export function reachabilityFromSourceExcludingBannedEdges(
    edges: TripDagEdgeLike[],
    bannedEdgeKeys: Set<string>,
    dagNodes: Array<{ index: number; kind: 'source' | 'event' | 'dest'; eventId?: string | null }>,
    getEventLabel: (eventId: string) => string
): ReachabilityDiagnostic {
    const adj = new Map<number, number[]>();
    for (const e of edges) {
        const k = edgeIndexKey(e.fromNodeIndex, e.toNodeIndex);
        if (bannedEdgeKeys.has(k)) continue;
        const arr = adj.get(e.fromNodeIndex) ?? [];
        arr.push(e.toNodeIndex);
        adj.set(e.fromNodeIndex, arr);
    }
    const seen = new Set<number>([0]);
    const stack = [0];
    while (stack.length > 0) {
        const u = stack.pop();
        if (u === undefined) throw new Error('collectUnreachableEventDemands: stack unexpectedly empty');
        for (const v of adj.get(u) ?? []) {
            if (!seen.has(v)) {
                seen.add(v);
                stack.push(v);
            }
        }
    }
    const unreachableEventIds: string[] = [];
    const unreachableEventLabels: string[] = [];
    for (const n of dagNodes) {
        if (n.kind !== 'event' || !n.eventId) continue;
        const reqIndex = n.index;
        if (!seen.has(reqIndex)) {
            unreachableEventIds.push(n.eventId);
            unreachableEventLabels.push(getEventLabel(n.eventId));
        }
    }
    return { unreachableEventIds, unreachableEventLabels, reachableNodeIndices: seen };
}
